import { Ref, useRef } from 'react';
import { cn, getUUID } from '@/lib';
import { StanToggleProps } from '../types';
import styles from './StanToggle.module.css';
import { StanLabel } from '../StanLabel/StanLabel';

interface StanTogglePropsWithRef extends StanToggleProps {
    ref?: Ref<HTMLInputElement>;
}

export const StanToggle = ({
    toggleSize = 'md',
    id = getUUID(),
    disabled = false,
    loading = false,
    checked = false,
    label,
    caption,
    placement = 'right',
    className,
    onChange,
    ref,
}: StanTogglePropsWithRef) => {
    // Create internal fallback ref
    const internalRef = useRef<HTMLInputElement>(null);

    // Use forwarded ref if provided, else fallback to internalRef
    const resolvedRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    // Determine size classes
    const getSizeClass = () => {
        switch (toggleSize) {
            case 'sm':
                return styles.switchSmall;
            case 'lg':
                return styles.switchLarge;
            default:
                return styles.switchMedium;
        }
    };

    const getSliderSizeClass = () => {
        switch (toggleSize) {
            case 'sm':
                return styles.sliderSmall;
            case 'lg':
                return styles.sliderLarge;
            default:
                return styles.sliderMedium;
        }
    };

    return (
        <div className={cn(styles.container, className)}>
            {label && placement === 'right' && <StanLabel label={label} caption={caption} />}

            {/* TODO : Change label to some other tag */}
            <label
                className={cn(styles.switch, getSizeClass(), disabled && styles.disabled, loading && styles.loading)}
            >
                <input
                    ref={resolvedRef}
                    id={id}
                    type='checkbox'
                    checked={checked}
                    onChange={onChange}
                    disabled={loading || disabled}
                    className={cn(styles.switchinput, loading && styles.loading, disabled && styles.disabled)}
                />
                <span className={cn(styles.slider, getSliderSizeClass())} />
            </label>
            {label && placement === 'left' && <StanLabel label={label} caption={caption} />}
        </div>
    );
};

StanToggle.displayName = 'StanToggle';
